import { computed, inject, Injectable, Signal } from '@angular/core';
import { Onboarding, OnboardingEditable, OnboardingState } from '@cassiopeia/integration';
import { propertySort, safeResource, truthy } from '@nestech/utils';
import { IntegrationApi } from '../danish-pension';
import { CanActivateFn, Router } from '@angular/router';
import { Authentication } from '../auth';
import { toObservable } from '@angular/core/rxjs-interop';
import { first, map } from 'rxjs';

@Injectable()
export class OnboardingService {
  #integration = inject(IntegrationApi);
  #resource = safeResource({
    loader: () =>
      this.#integration.api.member
        .onboardingListV1()
        .then(res => res.data)
        .then(onboarding => ({
          ...onboarding,
          pages: onboarding.pages?.toSorted(propertySort('order')),
        })),
  });
  onboarding: Signal<Onboarding | undefined> = this.#resource.value;
  state: Signal<OnboardingState> = computed(() => this.onboarding()?.onboardingState ?? 'NotAvailable');
  isLoading = this.#resource.isLoading;
  error = this.#resource.error;

  #intentionalLeave = false;

  update(onboarding: Partial<OnboardingEditable>) {
    return this.#integration.api.member.onboardingPartialUpdateV1(onboarding).then(() => this.#resource.reload());
  }

  markIntentionalLeave() {
    this.#intentionalLeave = true;
  }

  consumeIntentionalLeave(): boolean {
    const intentional = this.#intentionalLeave;
    this.#intentionalLeave = false;
    return intentional;
  }
}

export const rerouteToOnboarding = (): CanActivateFn => () => {
  const router = inject(Router);
  const service = inject(OnboardingService);
  if (inject(Authentication).isRestricted()) return true;
  const settled = toObservable(computed(() => !service.isLoading() && !!(service.onboarding() ?? service.error())));
  return settled.pipe(
    truthy(),
    map(() => (service.state() === 'Available' ? router.createUrlTree(['wizard/velkommen']) : true)),
    first(),
  );
};
